/**
 * UploadHub.jsx — Feb 9 2026: now 4 pills. Added "Text Series" as the
 * third option per user spec ("create a third upload option available
 * for series and make it sure that episodes can be uploaded in words").
 *
 * Visual language matches the sharp Library — dark cards with thin
 * jewel-tone borders + accent-bar on the right edge.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Headphones, ListPlus, Upload as UploadIcon, FileText, ChevronRight } from 'lucide-react';

const ACCENTS = {
  amber:   { ring: 'border-amber-400/35 hover:border-amber-300/60 focus-visible:ring-amber-300/40',     text: 'text-amber-300',   bar: 'from-amber-400 to-amber-200',       iconBg: 'bg-amber-400/15 border-amber-400/30' },
  fuchsia: { ring: 'border-fuchsia-400/35 hover:border-fuchsia-300/60 focus-visible:ring-fuchsia-300/40', text: 'text-fuchsia-300', bar: 'from-fuchsia-400 to-fuchsia-200',   iconBg: 'bg-fuchsia-400/15 border-fuchsia-400/30' },
  emerald: { ring: 'border-emerald-400/35 hover:border-emerald-300/60 focus-visible:ring-emerald-300/40', text: 'text-emerald-300', bar: 'from-emerald-400 to-emerald-200',   iconBg: 'bg-emerald-400/15 border-emerald-400/30' },
  indigo:  { ring: 'border-indigo-400/35 hover:border-indigo-300/60 focus-visible:ring-indigo-300/40',   text: 'text-indigo-300',  bar: 'from-indigo-400 to-indigo-200',     iconBg: 'bg-indigo-400/15 border-indigo-400/30' },
};

const BigPill = ({ id, label, sub, Icon, accent, onClick }) => {
  const A = ACCENTS[accent] || ACCENTS.amber;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`upload-pill-${id}`}
      className={`group relative w-full text-left rounded-2xl overflow-hidden border ${A.ring} bg-[#12131A] active:scale-[0.995] transition-[transform,border-color] focus:outline-none focus-visible:ring-2`}
    >
      <div className="px-5 py-7 sm:px-7 sm:py-9 flex items-center gap-5">
        <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-2xl border ${A.iconBg} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-7 h-7 sm:w-8 sm:h-8 ${A.text}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`eyebrow ${A.text}`}>Tap to open</p>
          <p className="font-display text-2xl sm:text-3xl font-black text-white tracking-tight leading-none mt-1">{label}</p>
          <p className="text-[11px] sm:text-xs text-white/55 mt-1.5">{sub}</p>
        </div>
        <ChevronRight className={`w-5 h-5 ${A.text} opacity-60 group-hover:opacity-100 transition-opacity`} />
      </div>
      <div className={`absolute right-0 top-0 h-full w-[3px] bg-gradient-to-b ${A.bar}`} />
    </button>
  );
};

const UploadHub = () => {
  const navigate = useNavigate();
  return (
    <main className="sharp-bg max-w-3xl mx-auto px-4 py-6 pb-32 min-h-[calc(100vh-120px)]" data-testid="upload-hub-page">
      <header className="mb-6" data-testid="upload-hub-header">
        <p className="eyebrow text-amber-300">Scribe studio</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black text-white tracking-tight leading-[1.02] mt-1">
          Upload<span className="text-amber-300">.</span>
        </h1>
      </header>
      <div className="flex flex-col gap-3" data-testid="upload-pill-gate">
        <BigPill
          id="text-ebook"
          label="Text & Ebooks"
          sub="A standalone book, story or text file"
          Icon={BookOpen}
          accent="amber"
          onClick={() => navigate('/upload-text-ebook')}
        />
        <BigPill
          id="audio-series"
          label="Audio Series"
          sub="Create a new audio series (cover · author · description)"
          Icon={Headphones}
          accent="fuchsia"
          onClick={() => navigate('/upload-audio-series')}
        />
        {/* Feb 9 2026 — NEW third option for SERIES: text-only episodes
            that readers can read, flash-read, chunk-read, or TTS. */}
        <BigPill
          id="text-series"
          label="Text Series"
          sub="Serialised writing · readers get flash, chunk + TTS playback"
          Icon={FileText}
          accent="indigo"
          onClick={() => navigate('/upload-text-series')}
        />
        <BigPill
          id="episodes"
          label="Upload Episodes"
          sub="Add the next episode to an existing series"
          Icon={ListPlus}
          accent="emerald"
          onClick={() => navigate('/upload-episodes')}
        />
      </div>
    </main>
  );
};

export default UploadHub;
